import { entryPointId } from 'settings/app/constants'
import textContext from 'settings/app/staticLabels.json'

/**
 * get contribution labels and tooltips
 * @param contribution
 * @param resultID
 * @param selectedType
 * @returns {{dataCy: string, careLevelText: string, hintTitle}}
 */
export const getContributionLabels = ({
  contribution,
  resultID,
  selectedType,
}) => {
  let careLevelText, dataCy, hintTitle

  switch (contribution.type) {
    case 'personalContribution':
      careLevelText = `(${ textContext[`generalInformation.careLevel`] } ${
        contribution.careLevel
      })`
      dataCy = `${ contribution.type }-${ resultID }-${ contribution.careLevel }`
      hintTitle =
        selectedType === entryPointId.CAREHOMES
          ? textContext[`generalInformation.${ contribution.type }.carehomes`]
          : textContext[`generalInformation.${ contribution.type }`]
      break

    case 'personalContributionShortTerm':
      careLevelText = textContext[
        'generalInformation.personalContribution.carehomes_shortTerm'
      ]
        .replace('__SEARCH__PRICE__', contribution.amount)
        .replace('__SEARCH__CARE__LEVEL__', contribution.careLevel)
      dataCy = `personalContribution-${ resultID }-${ contribution.careLevel }`
      break

    // daysandnightcare only
    case 'contributionDay':
    case 'contributionNight':
      careLevelText = `(${ textContext[`generalInformation.careLevel`] } ${
        contribution.careLevel
      })`
      dataCy = `${ contribution.type }-${ resultID }`
      hintTitle = textContext['generalInformation.dayAndNightCare']
        .replace(
          '__SEARCH__TERM__TYPE__',
          textContext[`generalInformation.dayAndNightCare.${ contribution.type }`]
        )
        .replace('__SEARCH__TERM__DAYS__', contribution.days)
      break
  }

  return { careLevelText, dataCy, hintTitle }
}

/**
 * search recursive in one compare hospital for one property
 * @param object
 * @param key
 * @returns {*}
 */
export const findCompareVal = (object, key) => {
  let value

  // eslint-disable-next-line array-callback-return
  Object.keys(object).some((k) => {
    if (k === key) {
      value = object[k]
      return true
    }

    if (object[k] && typeof object[k] === 'object') {
      value = findCompareVal(object[k], key)
      return value !== undefined
    }
  })

  return value
}

/**
 * triggers recursive search and compare found value
 * @param propPath
 * @param currentVal
 * @param comparingResults
 * @param bestIsLowest
 * @returns {boolean}
 */
export const compareSingleProperty = (
  propPath,
  currentVal,
  comparingResults,
  bestIsLowest = false
) => {
  let comparedValues = comparingResults.map((comparingResult) =>
    findCompareVal(comparingResult, propPath)
  )

  if (propPath === 'transparencyReport' || propPath === 'pricePerHour') {
    comparedValues = comparedValues.filter((compVal) => compVal !== null)

    if (comparedValues.length > 0) {
      comparedValues.forEach((compVal, index) => {
        comparedValues[index] = getParsedCompareString(compVal, propPath)
      })
    }

    currentVal = getParsedCompareString(currentVal, propPath)
  }

  return !bestIsLowest
    ? comparedValues.filter((val) => val > currentVal).length === 0
    : comparedValues.filter((val) => val < currentVal).length === 0
}

/**
 * access direct array values (no recursion, use for contribution array)
 * @param curKey
 * @param curVal
 * @param curIndex
 * @param careLevel
 * @param comparingResults
 * @param bestIsLowest
 * @returns {boolean}
 */
export const compareArrayProperty = (
  { curKey, curVal, curIndex, careLevel = false },
  comparingResults,
  bestIsLowest = false
) => {
  const comparedValues = []

  comparingResults.forEach((result) => {
    if (curKey === 'contribution' && result[curKey][curIndex]?.amount) {
      comparedValues.push(
        getParsedCompareString(
          result[curKey][curIndex].amount,
          'contributionAmount'
        )
      )
    }
    if (curKey === 'services') {
      comparedValues.push(result.services.length)
    }
  })

  // search for contribution with care level because not all results have the same number of contributions
  if (curKey === 'personalContributionShortTerm') {
    comparingResults.forEach((result) => {
      result.contribution.forEach((contrib) => {
        if (contrib.careLevel === careLevel) {
          comparedValues.push(
            getParsedCompareString(contrib.amount, 'contributionAmount')
          )
        }
      })
    })
  }

  if (curKey === 'contribution' || curKey === 'personalContributionShortTerm') {
    curVal = getParsedCompareString(curVal, 'contributionAmount')
  }

  return !!bestIsLowest
    ? comparedValues.filter((val) => val > curVal).length === 0
    : comparedValues.filter((val) => val < curVal).length === 0
}

/**
 * comparing qsr rating values from current selected hospitals
 * @param identifier
 * @param currentRating
 * @param comparingHospitals
 * @returns {boolean}
 */
export const compareIdentifier = (
  identifier,
  currentRating,
  comparingHospitals
) => {
  const comparedValues = []

  // get all qsr values by identifier
  comparingHospitals.forEach((comparingHospital) => {
    if (comparingHospital.qsr.length > 0) {
      comparingHospital.qsr.forEach((qsrValue) => {
        if (qsrValue.identifier === identifier) {
          comparedValues.push(qsrValue.rating)
        }
      })
    }
  })

  return currentRating >= Math.max(...comparedValues)
}

/**
 * return compare able float from string
 * @param compareString
 * @param type
 * @returns {number}
 */
export const getParsedCompareString = (compareString, type) => {
  let parsedString

  switch (type) {
    case 'contributionAmount':
      parsedString = parseFloat(
        compareString.replace(' €', '').replace(',', '.')
      )
      break
    case 'transparencyReport':
      parsedString = parseFloat(
        compareString.substring(compareString.indexOf('(') + 1).replace(')', '')
      )
      break
    case 'pricePerHour':
      parsedString = parseFloat(
        compareString
          .replace('ab ', '')
          .replace('€ / Stunde ', '')
          .replace(',', '.')
      )
      break
  }

  return parsedString
}
